/**
 * WebSocket Context
 *
 * Provides a single shared WebSocket connection to all consumers.
 * Any component can access `addMessageListener` and connection status
 * without prop drilling.
 *
 * Architecture:
 *   WebSocketProvider (connection owner)
 *     → useWebSocketContext() consumers:
 *       → useDerivedAssetEvents
 *       → useUnifiedEventConsumer
 *       → useExecutionEventConsumer
 */

import React, { createContext, useContext, useMemo } from "react";
import { useWebSocket } from "@/hooks/useWebSocket";

type MessageListener = (message: any) => void;
type AddMessageListener = (listener: MessageListener) => () => void;

interface WebSocketContextValue {
  addMessageListener: AddMessageListener;
  isConnected: boolean;
  status: "disconnected" | "connecting" | "connected";
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

export function WebSocketProvider({ children }: { children: React.ReactNode }) {
  const { addMessageListener, status } = useWebSocket();

  // Memoize context value to prevent unnecessary re-renders of consumers
  // when the provider re-renders but the actual values haven't changed.
  const value = useMemo<WebSocketContextValue>(
    () => ({
      addMessageListener,
      isConnected: status === "connected",
      status,
    }),
    [addMessageListener, status],
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

/**
 * Access the shared WebSocket connection.
 * Must be used within a WebSocketProvider.
 */
export function useWebSocketContext(): WebSocketContextValue {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    throw new Error(
      "useWebSocketContext must be used within a WebSocketProvider",
    );
  }
  return ctx;
}
